import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert';
import { Button } from '@repo/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  type DialogPrimitive,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { startRegistration } from '@simplewebauthn/browser';
import { KeyRoundIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { UAParser } from 'ua-parser-js';
import { z } from 'zod';
import { MAXIMUM_PASSKEYS } from '@/lib/auth-config';
import { trpc } from '@/providers/trpc';

export type PasskeyCreateDialogProps = {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
} & Omit<DialogPrimitive.DialogProps, 'children'>;

const ZCreatePasskeyFormSchema = z.object({
  passkeyName: z.string().min(3),
});

type TCreatePasskeyFormSchema = z.infer<typeof ZCreatePasskeyFormSchema>;

const parser = new UAParser();

export const PasskeyCreateDialog = ({ trigger, onSuccess, ...props }: PasskeyCreateDialogProps) => {
  useLocale();

  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<TCreatePasskeyFormSchema>({
    resolver: zodResolver(ZCreatePasskeyFormSchema, { error: validationError }),
    defaultValues: {
      passkeyName: '',
    },
  });

  const { mutateAsync: createPasskeyRegistrationOptions, isPending } =
    trpc.auth.createPasskeyRegistrationOptions.useMutation();

  const { mutateAsync: createPasskey } = trpc.auth.createPasskey.useMutation();

  const onFormSubmit = async ({ passkeyName }: TCreatePasskeyFormSchema) => {
    setFormError(null);

    try {
      const passkeyRegistrationOptions = await createPasskeyRegistrationOptions();

      const registrationResult = await startRegistration({
        optionsJSON: passkeyRegistrationOptions,
      });

      await createPasskey({
        passkeyName,
        verificationResponse: registrationResult,
      });

      toast.success(t('workspace.successfullyCreatedPasskey'), {
        duration: 5000,
      });

      onSuccess?.();
      setOpen(false);
    } catch (err) {
      if (!(err instanceof Error)) {
        return;
      }
      if (err.name === 'NotAllowedError') {
        return;
      }

      const error = AppError.parseError(err);
      setFormError(error.code);
    }
  };

  const extractDefaultPasskeyName = () => {
    if (!window?.navigator) {
      return;
    }

    parser.setUA(window.navigator.userAgent);

    const result = parser.getResult();
    const operatingSystem = result.os.name;
    const browser = result.browser.name;

    let passkeyName = '';

    if (operatingSystem && browser) {
      passkeyName = `${browser} (${operatingSystem})`;
    }

    return passkeyName;
  };

  useEffect(() => {
    if (!open) {
      const defaultPasskeyName = extractDefaultPasskeyName();

      form.reset({
        passkeyName: defaultPasskeyName,
      });

      setFormError(null);
    }
  }, [open, form]);

  return (
    <Dialog
      {...props}
      onOpenChange={(value) => !form.formState.isSubmitting && setOpen(value)}
      open={open}
    >
      <DialogTrigger asChild={true} onClick={(e) => e.stopPropagation()}>
        {trigger ?? (
          <Button loading={isPending} variant="secondary">
            <KeyRoundIcon className="me-1 -ml-1 h-5 w-5" />
            {t('workspace.addPasskey')}{' '}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspace.addPasskey')}</DialogTitle>
          <DialogDescription className="mt-4">
            {t('workspace.passkeysAllowYouToSignInAndAuthenticateUsing')}{' '}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)}>
            <fieldset
              className="flex h-full flex-col space-y-4"
              disabled={form.formState.isSubmitting}
            >
              <FormField
                control={form.control}
                name="passkeyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('workspace.passkeyName')}</FormLabel>
                    <FormControl>
                      <Input
                        className="bg-background"
                        placeholder={t('workspace.egMac')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Alert variant="default">
                <AlertDescription>
                  {t('workspace.whenYouClickContinueYouWillBePromptedTo')}{' '}
                </AlertDescription>
                <AlertDescription className="mt-2">
                  {t('workspace.ifYouDoNotWantToUseTheAuthenticator')}{' '}
                </AlertDescription>
              </Alert>

              {formError && (
                <Alert variant="destructive">
                  {match(formError)
                    .with('ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED', () => (
                      <AlertDescription>
                        {t('workspace.thisPasskeyHasAlreadyBeenRegistered')}
                      </AlertDescription>
                    ))
                    .with('TOO_MANY_PASSKEYS', () => (
                      <AlertDescription>
                        {t('workspace.youCannotHaveMoreThan')} {MAXIMUM_PASSKEYS}{' '}
                        {t('workspace.passkeys')}{' '}
                      </AlertDescription>
                    ))
                    .with('InvalidStateError', () => (
                      <>
                        <AlertTitle className="text-sm">
                          {t('workspace.passkeyCreationCancelledDueToOneOfTheFollowing')}{' '}
                        </AlertTitle>
                        <AlertDescription>
                          <ul className="mt-1 list-inside list-disc">
                            <li>{t('workspace.cancelledByUser')}</li>
                            <li>
                              {t('workspace.passkeyAlreadyExistsForTheProvidedAuthenticator')}
                            </li>
                            <li>{t('workspace.exceededTimeout')}</li>
                          </ul>
                        </AlertDescription>
                      </>
                    ))
                    .otherwise(() => (
                      <AlertDescription>
                        {t('workspace.somethingWentWrongPleaseTryAgainOrContactSupport')}{' '}
                      </AlertDescription>
                    ))}
                </Alert>
              )}

              <DialogFooter>
                <Button onClick={() => setOpen(false)} type="button" variant="secondary">
                  {t('reports.cancel')}{' '}
                </Button>

                <Button loading={form.formState.isSubmitting} type="submit">
                  {t('auth.continue')}{' '}
                </Button>
              </DialogFooter>
            </fieldset>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
